import React, { useEffect, useState } from 'react';
import { Box, Button, Rating, TextField, Typography } from '@mui/material';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { type SandboxCourseRating } from '../../utils/sandboxCourseApi';
import { upsertSandboxCourseRating } from '../../redux/sandbox/actions';

type Props = {
    courseId: string;
    ratingAvg?: number;
    ratingCount?: number;
    myRating?: SandboxCourseRating | null;
    canRate?: boolean;
    onRated?: (next: { rating_avg: number; rating_count: number; my_rating: SandboxCourseRating }) => void;
};

/**
 * Course page: show aggregate stars + let enrolled students rate the teacher/course.
 */
export function SandboxCourseRatingPanel({
    courseId,
    ratingAvg = 0,
    ratingCount = 0,
    myRating = null,
    canRate = false,
    onRated,
}: Props) {
    const dispatchPromise = useDispatchPromise();
    const [value, setValue] = useState<number | null>(myRating?.rating || null);
    const [review, setReview] = useState(myRating?.review || '');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setValue(myRating?.rating || null);
        setReview(myRating?.review || '');
    }, [myRating?.rating, myRating?.review, myRating?._id]);

    const submit = async () => {
        if (!value) {
            showToastMessage('Pick a star rating', 'info');
            return;
        }
        setBusy(true);
        try {
            const res: any = await dispatchPromise(
                upsertSandboxCourseRating({
                    courseId,
                    body: { rating: value, review: review.trim() },
                }),
            );
            showToastMessage(myRating ? 'Rating updated' : 'Thanks for rating', 'success');
            onRated?.({
                rating_avg: Number(res?.rating_avg) || value,
                rating_count: Number(res?.rating_count) || ratingCount,
                my_rating: res?.rating || { rating: value, review: review.trim() },
            });
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not save rating', 'error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: OBSIDIAN.surfaceContainer,
                mb: 3,
            }}>
            <Typography
                sx={{
                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                    fontWeight: 700,
                    fontSize: 16,
                    color: OBSIDIAN.onSurface,
                }}>
                Student rating
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                <Rating
                    value={Number(ratingAvg) || 0}
                    precision={0.1}
                    readOnly
                    sx={{
                        '& .MuiRating-iconFilled': { color: OBSIDIAN.primary },
                        '& .MuiRating-iconEmpty': { color: `${OBSIDIAN.outline}88` },
                    }}
                />
                <Typography sx={{ fontSize: 13, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                    {ratingCount > 0
                        ? `${Number(ratingAvg).toFixed(1)} · ${ratingCount} rating${ratingCount === 1 ? '' : 's'}`
                        : 'No ratings yet'}
                </Typography>
            </Box>

            {canRate ? (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Typography sx={{ fontSize: 13, color: OBSIDIAN.onSurface, fontFamily: OBSIDIAN_BODY_FONT }}>
                        {myRating ? 'Update your rating' : 'Rate this course'}
                    </Typography>
                    <Rating
                        value={value}
                        onChange={(_, next) => setValue(next)}
                        sx={{
                            '& .MuiRating-iconFilled': { color: OBSIDIAN.primary },
                            '& .MuiRating-iconEmpty': { color: `${OBSIDIAN.outline}88` },
                        }}
                    />
                    <TextField
                        size="small"
                        multiline
                        minRows={2}
                        placeholder="Optional feedback for the teacher"
                        value={review}
                        onChange={e => setReview(e.target.value.slice(0, 500))}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: OBSIDIAN.surfaceContainerHigh,
                                borderRadius: 2,
                                color: OBSIDIAN.onSurface,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
                            },
                        }}
                    />
                    <Button
                        disabled={busy || !value}
                        onClick={() => void submit()}
                        sx={{
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: OBSIDIAN.primary,
                            color: OBSIDIAN.onPrimaryFixed,
                            borderRadius: 999,
                            px: 2,
                            '&:hover': { filter: 'brightness(1.05)' },
                        }}>
                        {busy ? 'Saving…' : myRating ? 'Update rating' : 'Submit rating'}
                    </Button>
                </Box>
            ) : null}
        </Box>
    );
}
